<template lang='pug'>
  div.component-container(
      ref="componentContainer"
      tabindex="3"
      :style="{margin: canvasPadding+'px'}")
    canvas#timelineCanvas.timeline-canvas
</template>

<script>
import Vue from 'vue'
import { fabric } from 'fabric'
import AnimationConstant from '@/constants/AnimationConstant'
import TimelineTextMixin from './TimelineTextMixin'
import TimelineDotsMixin from './TimelineDotsMixin'
import TimelineScrollMixin from './TimelineScrollMixin'

const getTimeString = f => f.toFixed(2)

const { MAX_FRAME_COUNT } = AnimationConstant

const DeleteBackspaceKeySet = new Set(['Delete', 'Backspace'])
const NavigationKeySet = new Set(['ArrowUp', 'ArrowLeft', 'ArrowRight', 'Comma', 'Period'])
const NavigatePreviousKeySet = new Set(['ArrowLeft', 'Comma'])

export default {
  name: 'KeyframeTimeline',
  mixins: [TimelineTextMixin, TimelineDotsMixin, TimelineScrollMixin],
  props: {
    value: Array,
    canvasPadding: {
      type: Number,
      default: 16
    },
    timelineFrames: {
      type: Number,
      default: 30
    },
    frameRange: Object,
    enableDebugging: Boolean
  },
  data: function () {
    return {
      fps: 30,
      keyframeIndexesCache: [],
      keyframeIndexesCacheSet: new Set(),
      hoverKeyframeIndex: -1,
      selectedFrameIndexes: new Set(),
      dragFrameShift: 0,
      operationCounter: 1,
      operationId: 0,
      // Active frame index. Cannot select until keyframes are requested.
      // TODO Think if this architecture presents any bugs
      activeFrameIndexCache: -1
    }
  },
  computed: {
    fpsInverse () {
      return this.fps > 0 ? 1 / this.fps : 1
    },
    frameRangeDelta () {
      const delta = this.frameRange.max - this.frameRange.min
      return Math.max(1, delta)
    },
    frameTimeTexts () {
      let { min, max } = this.frameRange
      min = Math.max(1, min)
      max = Math.max(min + 1, max)
      const fpsInverse = this.fpsInverse
      const timeMin = (min - 1) * fpsInverse
      const timeMax = (max - 1) * fpsInverse
      return {
        minFrame: `${min}`,
        maxFrame: `${max}`,
        minTime: getTimeString(timeMin),
        maxTime: getTimeString(timeMax)
      }
    }
  },
  watch: {
    value () {
      let isShifted = false
      if (this.operationId) {
        const cache = this._operatingCache || {}
        const { id, eventName, data } = cache
        if (eventName === 'shift-frames' && id === this.operationId) {
          const { deltaFrames, indexes } = data
          const shiftSelected = new Set()
          for (const frameIndex of indexes) {
            const shiftedFrameIndex = frameIndex + deltaFrames
            shiftSelected.add(shiftedFrameIndex)
          }
          this.selectedFrameIndexes = shiftSelected
          isShifted = true
        }
      }
      this._operatingCache = null
      this.operationId = 0

      if (!isShifted && this.activeFrameIndexCache >= 0) {
        this.selectedFrameIndexes = new Set([this.activeFrameIndexCache])
      }
      this.activeFrameIndexCache = -1

      this.refreshKeyframeIndexes()
      this.updateOperatingView()

      if (isShifted) {
        this.publishSelectedSingleFrame()
      }
    },
    // This is for syncing with on-frame-range-change event. Call to syncFrameRange if externally setting frame range.
    frameRange () {
      this.frameTimeTextCache = this.frameTimeTexts
    }
  },
  mounted () {
    const container = this.$el
    const width = container.clientWidth
    this.canvas = new fabric.Canvas('timelineCanvas', {
      backgroundColor: 'rgb(0,0,0)',
      // Built in selection is not so customizable. Must code custom selection.
      selection: false,
      width,
      height: container.clientHeight,
      fireMiddleClick: true
    })

    this.frameTimeTextCache = this.frameTimeTexts

    this.setCanvasDimensions()
    this.offsetCanvas()

    this.configureCanvasEvents()

    this.createFrameDots()
    this.createTexts()

    this.createSelectionRectangle()

    this.updateFrameTimeTexts()

    this.refreshKeyframeIndexes()

    document.addEventListener('keydown', this.onKeyDown)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeyDown)
  },
  methods: {
    // Do not move into computed when data involves non-primitive
    hasSelection () {
      return this.selectedFrameIndexes.size > 0
    },
    // Do not move into computed when data involves non-primitive
    isDragResultPossible () {
      if (this.dragFrameShift && this.hasSelection()) {
        for (const frameIndex of this.selectedFrameIndexes) {
          const targetFrameIndex = frameIndex + this.dragFrameShift
          if (this.keyframeIndexesCacheSet.has(targetFrameIndex) &&
              !this.selectedFrameIndexes.has(targetFrameIndex)) {
            return false
          }
        }
      }
      return true
    },
    revertOperation (operationId) {
      if (operationId !== this.operationId) { return }

      this.operationId = null
      this.refreshKeyframeIndexes()
      this.updateOperatingView()
    },
    onEditorFrameIndex (frameIndex, cacheOnNextChange) {
      if (this.hasSelection()) {
        if (!this.selectedFrameIndexes.has(frameIndex)) {
          const selection = this.keyframeIndexesCacheSet.has(frameIndex) ? frameIndex : []
          this.setSelected(selection)
        }
      }

      if (!this.hasSelection() && this.keyframeIndexesCacheSet.has(frameIndex)) {
        this.setSelected(frameIndex)
      }

      if (cacheOnNextChange) {
        this.activeFrameIndexCache = frameIndex
      }
    },
    setSelected (selectedIndexes) {
      if (!selectedIndexes && selectedIndexes !== 0) { return }

      if (!Array.isArray(selectedIndexes)) {
        selectedIndexes = [selectedIndexes]
      }

      let isValid = true
      for (const index of selectedIndexes) {
        if (!this.keyframeIndexesCacheSet.has(index)) {
          isValid = false
          break
        }
      }
      if (isValid) {
        this.selectedFrameIndexes = new Set(selectedIndexes)
      }
      this.updateKeyframeViews()
    },
    syncFrameRange () {
      const { padding, keyframeWidths } = this.canvasDimensions
      const frameIndex = this.frameRange.min - 1
      const pannedX = frameIndex > 0 ? -(padding + frameIndex * keyframeWidths.full) : 0
      this.canvas.viewportTransform[4] = Math.min(pannedX, 0)
      console.log('sync frame range', frameIndex, pannedX)
      this.updateFrameDots()
      this.updateFrameTimeTexts()
      this.updateKeyframeViews()
    },
    onKeyDown (e) {
      if (e.target !== this.$refs.componentContainer) { return }

      if (DeleteBackspaceKeySet.has(e.key)) {
        if (this.hasSelection()) {
          this.sendOperation('delete-frames', {
            indexes: Array.from(this.selectedFrameIndexes)
          })
        }
      } else if (NavigationKeySet.has(e.key)) {
        switch (e.key) {
          case 'ArrowUp':
            this.$emit('toggle-play-pause')
            break
          case 'ArrowLeft':
          case 'Comma':
          case 'ArrowRight':
          case 'Period':
            this.processKeyboardNavigate(e)
            break
        }
      }
    },
    publishSelectionChange () {
      // Not used
    },
    publishSelectedSingleFrame () {
      if (this.selectedFrameIndexes.size === 1) {
        this.sendOperation('on-active-frame-index', {
          frameIndex: Array.from(this.selectedFrameIndexes)[0]
        })
      }
    },
    processKeyboardNavigate (e) {
      const seekDirection = NavigatePreviousKeySet.has(e.key) ? -1 : 1
      const isSeekNavigate = e.shiftKey
      const isSeekStartEnd = isSeekNavigate && e.ctrlKey
      const isSeekOrientKeyFrame = !isSeekStartEnd && isSeekNavigate && e.altKey
      const isSeekKeyFrame = !(isSeekStartEnd || isSeekOrientKeyFrame) && isSeekNavigate
      this.$emit('keyboard-navigate', {
        direction: seekDirection,
        startEnd: isSeekStartEnd,
        orient: isSeekOrientKeyFrame,
        keyframe: isSeekKeyFrame
      })
    },
    setCanvasDimensions () {
      const { width, height } = this.canvas
      const padding = height * 0.05
      const paddingHeight = padding
      const textRowHeight = height * 0.3
      const keyframeRowHeight = height - textRowHeight - paddingHeight * 4
      const textRowY = paddingHeight
      const keyframeRowY = textRowY + textRowHeight + paddingHeight * 2
      const keyframeCenterY = keyframeRowY + keyframeRowHeight * 0.5
      const secondaryTextHeight = textRowHeight * 0.75
      const secondaryTextY = textRowY + (textRowHeight - secondaryTextHeight) * 0.5

      this.canvasDimensions = {
        padding,
        width,
        height,
        rowHeights: {
          text: textRowHeight,
          secondaryText: secondaryTextHeight,
          keyframe: keyframeRowHeight
        },
        ys: {
          text: textRowY,
          secondaryText: secondaryTextY,
          keyframe: {
            top: keyframeRowY,
            center: keyframeCenterY
          }
        },
        xs: {},
        autoscroll: {},
        keyframeWidths: {}
      }

      this.updateKeyframeWidth()
    },
    updateKeyframeWidth (deltaScale = 0) {
      let rangeDelta = this.frameRangeDelta
      const minRange = 15
      if (rangeDelta === minRange && deltaScale < 0) { return false }
      const maxRange = 180
      if (rangeDelta === maxRange && deltaScale > 0) { return false }
      if (rangeDelta < minRange) {
        rangeDelta = minRange
        deltaScale = 0
      } else if (rangeDelta > maxRange) {
        rangeDelta = maxRange
        deltaScale = 0
      }

      const { width, padding, xs, keyframeWidths } = this.canvasDimensions

      const baseWidth = width / (this.timelineFrames + 1)
      const dotWidth = baseWidth * 0.2

      // Scaling can get stuck due to outer bounding operations when frame range is small and scaling down
      if (deltaScale > 0) {
        if (rangeDelta < 20) {
          deltaScale *= 2
        }
      }

      rangeDelta *= (1 + deltaScale)
      rangeDelta = Math.max(1, rangeDelta)
      let fullWidth = width / rangeDelta
      fullWidth = Math.max(Math.min(fullWidth, width * 0.2), baseWidth * 0.15)
      const viewWidth = fullWidth * 0.8
      const horizontalPaddingHalf = (fullWidth - viewWidth) * 0.5

      const firstKeyframeCenterX = padding + fullWidth * 0.5

      xs.firstKeyframeCenterX = firstKeyframeCenterX

      keyframeWidths.full = fullWidth
      keyframeWidths.view = viewWidth
      keyframeWidths.inverseFull = 1 / fullWidth
      keyframeWidths.dot = dotWidth
      keyframeWidths.horizontalPaddingHalf = horizontalPaddingHalf

      this.setAutoscrollCanvasDimensions()

      return true
    },
    offsetCanvas () {
      const { padding, keyframeWidths } = this.canvasDimensions
      const minFrame = this.frameRange.min
      const offset = minFrame > 1 ? Math.min(0, -padding - keyframeWidths.full * minFrame) : 0
      this.canvas.viewportTransform[4] = offset
    },

    configureCanvasEvents () {
      const canvas = this.canvas

      canvas.on('mouse:wheel', (opt) => {
        const { e } = opt
        e.stopPropagation()

        const deltaScale = opt.e.deltaY * 0.0002

        const pointerX = opt.pointer.x
        const cursorFrameIndex = this.getCursorFrameIndex(pointerX)

        if (!this.updateKeyframeWidth(deltaScale)) { return }

        // Clamp panning between min and max frames
        const { padding, width: canvasWidth, keyframeWidths } = this.canvasDimensions
        const fullCursorFrameX = padding + (cursorFrameIndex + 0.5) * keyframeWidths.full
        let canvasPanX = Math.min(0, pointerX - fullCursorFrameX)
        canvasPanX = Math.max(canvasPanX, -padding - MAX_FRAME_COUNT * keyframeWidths.full + canvasWidth)
        canvas.viewportTransform[4] = canvasPanX

        this.updateFrameDots()
        this.updateFrameRange()
        this.updateCursorFrame(opt.pointer.x)
        this.updateFrameTimeTexts()
        this.updateKeyframeViews()
      })

      let isPanning = false
      let isMakingSelection = false
      let isDraggingSelection = false

      const getUnderCursorIndexes = ({ x, y }) => {
        const frameIndex = this.getCursorFrameIndex(x)
        const indexes = { frame: frameIndex, keyframe: -1 }

        const { ys, rowHeights } = this.canvasDimensions
        const keyframeY = ys.keyframe.top
        const keyframeHeight = rowHeights.keyframe
        if (y >= keyframeY && y <= keyframeY + keyframeHeight) {
          indexes.keyframe = this.keyframeIndexesCacheSet.has(frameIndex) ? frameIndex : -1
        }

        return indexes
      }

      const mouseDownProperties = {
        downButton: -1,
        coords: {},
        canvasX: {},
        cursorIndexes: { frame: -1, keyframe: -1 }
      }

      canvas.on('mouse:down', (opt) => {
        const { e } = opt
        e.stopPropagation()

        // Ignore simultaneous mouse button clicks
        if (isMakingSelection || isDraggingSelection || isPanning) {
          Vue.logger.log('Mouse down while mid sequence', isMakingSelection, isDraggingSelection, isPanning)
          return
        }

        const vpt = canvas.viewportTransform
        const isModified = e.altKey || e.ctrlKey || e.shiftKey
        if (isModified) { return }

        const coords = opt.pointer
        mouseDownProperties.coords = coords
        mouseDownProperties.canvasX = vpt[4]
        mouseDownProperties.cursorIndexes = {}

        this.hoverKeyframeIndex = -1

        mouseDownProperties.downButton = opt.button
        if (opt.button === 2) {
          isPanning = true
        } else {
          if (this.operationId) { return }

          const cursorIndexes = getUnderCursorIndexes(coords)
          mouseDownProperties.cursorIndexes = cursorIndexes

          const keyframeIndex = cursorIndexes.keyframe
          if (keyframeIndex >= 0 && this.selectedFrameIndexes.has(keyframeIndex)) {
            // Dragging
            isDraggingSelection = true
            updateDragState(cursorIndexes)
          } else {
            // Select(ing)
            this.selectedFrameIndexes.clear()
            if (keyframeIndex < 0) {
              isMakingSelection = true
            } else {
              this.selectedFrameIndexes.add(keyframeIndex)
            }
          }

          this.updateSelectionView({ corner1: coords, corner2: coords })
          this.updateKeyframeViews()
        }
        this.publishSelectionChange()
      })

      const updateDragState = (cursorIndexes) => {
        this.dragFrameShift = 0
        if (isDraggingSelection && this.hasSelection()) {
          const downFrameIndex = mouseDownProperties.cursorIndexes.keyframe
          this.dragFrameShift = downFrameIndex >= 0 ? cursorIndexes.frame - downFrameIndex : 0
        }
      }

      const isCursorYSelecting = (y1, y2) => {
        const { ys, rowHeights } = this.canvasDimensions
        const yTop = ys.keyframe.top
        const yBottom = yTop + rowHeights.keyframe
        return (y1 >= yTop && y1 <= yBottom) || (y2 >= yTop && y2 <= yBottom) || (Math.min(y1, y2) < yTop && Math.max(y1, y2) > yBottom)
      }

      canvas.on('mouse:move', (opt) => {
        const { e } = opt
        e.stopPropagation()

        const coords = opt.pointer

        if (isPanning) {
          const x0 = mouseDownProperties.canvasX

          const deltaX = coords.x - mouseDownProperties.coords.x
          let pannedX = x0 + deltaX
          if (pannedX > 0) { return }

          // Clamp at max frames
          const vpt = canvas.viewportTransform
          const { padding, width: canvasWidth, keyframeWidths } = this.canvasDimensions
          const maxFrameX = padding + MAX_FRAME_COUNT * keyframeWidths.full
          const endCanvasPannedX = -vpt[4] - deltaX + canvasWidth
          if (endCanvasPannedX > maxFrameX) {
            pannedX = canvasWidth - maxFrameX
          }

          vpt[4] = pannedX
          this.updateFrameRange()
          this.updateFrameTimeTexts()
        } else {
          if (this.operationId) { return }

          const cursorIndexes = getUnderCursorIndexes(coords)

          if (mouseDownProperties.downButton < 0) {
            this.hoverKeyframeIndex = cursorIndexes.keyframe
          } else if (isDraggingSelection) {
            updateDragState(cursorIndexes)
          } else if (isMakingSelection) {
            const downIndex = mouseDownProperties.cursorIndexes.frame
            const frameIndex = cursorIndexes.frame
            if (downIndex >= 0 && frameIndex >= 0) {
              const selectedIndexes = this.selectedFrameIndexes
              selectedIndexes.clear()

              if (isCursorYSelecting(coords.y, mouseDownProperties.coords.y)) {
                const minIndex = Math.min(frameIndex, downIndex)
                const maxIndex = Math.max(frameIndex, downIndex)
                const rangeCount = maxIndex - minIndex
                const keyframeCount = this.keyframeIndexesCache.length
                if (rangeCount < keyframeCount) {
                  const indexSet = this.keyframeIndexesCacheSet
                  for (let i = minIndex; i <= maxIndex; i++) {
                    if (indexSet.has(i)) {
                      selectedIndexes.add(i)
                    }
                  }
                } else {
                  for (const i of this.keyframeIndexesCache) {
                    if (i >= minIndex && i <= maxIndex) {
                      selectedIndexes.add(i)
                    }
                  }
                }
              }

              this.updateSelectionView({
                corner1: mouseDownProperties.coords,
                corner2: coords
              })
            }
          } else if (this.selectedFrameIndexes.size > 0) {
            isDraggingSelection = true
            updateDragState(cursorIndexes)
          }

          if (mouseDownProperties.downButton === 1) {
            this.setAutoscroll(coords.x)
          } else {
            this.clearAutoscroll()
          }

          this.updateCursorFrame(coords.x)
          this.updateFrameTimeTexts()
          this.updateKeyframeViews()
        }
        this.publishSelectionChange()
      })
      canvas.on('mouse:out', () => {
        if (mouseDownProperties.downButton < 0) {
          this.hoverKeyframeIndex = -1
          this.clearCursorFrame()
          this.updateFrameTimeTexts()
          this.updateKeyframeViews()
        }
      })
      canvas.on('mouse:up', (opt) => {
        if (mouseDownProperties.downButton !== opt.button) { return }
        mouseDownProperties.downButton = -1

        const { e } = opt
        e.stopPropagation()

        const coords = opt.pointer
        const cursorIndexes = getUnderCursorIndexes(coords)

        // Hover if mouse up over a keyframe
        this.hoverKeyframeIndex = cursorIndexes.keyframe

        if (opt.button === 2) {
          isPanning = false
        } else {
          this.clearAutoscroll()

          if (isDraggingSelection && this.dragFrameShift && this.isDragResultPossible()) {
            this.sendOperation('shift-frames', {
              deltaFrames: this.dragFrameShift,
              indexes: Array.from(this.selectedFrameIndexes)
            })
            this.updateKeyframeViews()
          } else if (this.hasSelection()) {
            this.publishSelectedSingleFrame()
            this.updateKeyframeViews()
          }

          isMakingSelection = false
          isDraggingSelection = false
          updateDragState({})
          this.updateSelectionView()
        }
        this.publishSelectionChange()
      })
    },

    addToCanvas (adds) {
      if (!adds) { return }

      if (typeof adds === 'object') {
        adds = Object.values(adds)
      }
      const canvas = this.canvas
      adds.forEach(a => canvas.add(a))
    },

    getCursorFrameIndex (cursorX) {
      const canvasPanX = this.canvas.viewportTransform[4]
      const { padding, keyframeWidths } = this.canvasDimensions
      const inverseKeyframeWidth = keyframeWidths.inverseFull
      const canvasFrameStartX = -canvasPanX - padding
      return Math.floor((canvasFrameStartX + cursorX) * inverseKeyframeWidth)
    },

    updateCursorFrame (cursorX) {
      const cursorFrameIndex = Math.min(Math.max(this.getCursorFrameIndex(cursorX), 0), MAX_FRAME_COUNT)
      this.frameTimeTextCache.cursorFrame = `${cursorFrameIndex + 1}`
      const cursorFrameText = getTimeString(cursorFrameIndex * this.fpsInverse)
      this.frameTimeTextCache.cursorTime = cursorFrameText
    },

    clearCursorFrame () {
      this.frameTimeTextCache.cursorFrame = ''
      this.frameTimeTextCache.cursorTime = ''
    },

    updateFrameRange () {
      const canvasPanX = this.canvas.viewportTransform[4]
      const { padding, width, keyframeWidths } = this.canvasDimensions
      const inverseKeyframeWidth = keyframeWidths.inverseFull
      const canvasFrameStartX = -canvasPanX - padding
      const firstVisibleFrame = Math.floor(canvasFrameStartX * inverseKeyframeWidth) + 1
      const lastVisibleFrame = Math.floor((canvasFrameStartX + width) * inverseKeyframeWidth) + 1
      this.$emit('on-frame-range-change', {
        min: Math.max(firstVisibleFrame, 1),
        max: Math.min(lastVisibleFrame, MAX_FRAME_COUNT)
      })
    },

    refreshKeyframeIndexes () {
      if (!this.canvas) { return }

      const keyframeIndexes = this.value.slice()
      if (!this.canvasKeyframes) {
        this.canvasKeyframes = []
      }
      const additionalKeyframeCount = keyframeIndexes.length - this.canvasKeyframes.length
      const { rowHeights, ys, keyframeWidths } = this.canvasDimensions
      const additionalKeyframeViews = []
      for (let i = 0; i < additionalKeyframeCount; i++) {
        const keyframeView = new fabric.Rect({
          width: keyframeWidths.view,
          height: rowHeights.keyframe,
          rx: 2,
          ry: 2,
          fill: '#9b71f5',
          top: ys.keyframe.top,
          selectable: false,
          evented: false
        })
        this.canvasKeyframes.push(keyframeView)
        additionalKeyframeViews.push(keyframeView)
      }

      this.addToCanvas(additionalKeyframeViews)

      this.keyframeIndexesCache = keyframeIndexes
      const cacheSet = new Set(keyframeIndexes)
      this.keyframeIndexesCacheSet = cacheSet

      // Clear dependant state if invalid due to changed indexes
      if (~this.hoverKeyframeIndex && !cacheSet.has(this.hoverKeyframeIndex)) {
        this.hoverKeyframeIndex = -1
      }

      if (this.selectedFrameIndexes.size > 0) {
        let isSelectionValid = true
        for (const frameIndex of this.selectedFrameIndexes) {
          if (!cacheSet.has(frameIndex)) {
            isSelectionValid = false
            break
          }
        }
        if (!isSelectionValid) {
          this.selectedFrameIndexes = new Set()
        }
      }
      this.publishSelectionChange()

      this.dragFrameShift = 0

      this.clearAutoscroll()

      this.updateKeyframeViews()
    },

    updateOperatingView () {
      const isInOperation = !!this.operationId
      this.canvas.backgroundColor = isInOperation ? '#181818' : '#000'
      this.canvas.requestRenderAll()
    },

    updateKeyframeViews () {
      const keyframeIndexes = this.keyframeIndexesCache
      const { padding, keyframeWidths } = this.canvasDimensions

      const hPaddingHalf = keyframeWidths.horizontalPaddingHalf

      let i = 0
      const isDragging = this.dragFrameShift !== 0
      const isDragValid = this.isDragResultPossible()
      for (const frameIndex of keyframeIndexes) {
        const keyframeView = this.canvasKeyframes[i]
        const isSelected = this.selectedFrameIndexes.has(frameIndex)
        const isHovering = frameIndex === this.hoverKeyframeIndex

        let targetFrameIndex = frameIndex
        if (isSelected && isDragging) {
          targetFrameIndex += this.dragFrameShift
        }

        let fill = '#9b71f5'
        if (isSelected) {
          fill = '#fdd835'
          if (!isDragValid) {
            fill = '#bf360c'
            this.canvas.bringToFront(keyframeView)
          }
        } else if (isHovering) {
          fill = '#fff176'
        }
        keyframeView.fill = fill

        keyframeView.visible = true
        keyframeView.scaleX = keyframeWidths.view / keyframeView.width

        const left = padding + targetFrameIndex * keyframeWidths.full + hPaddingHalf
        keyframeView.left = left

        keyframeView.setCoords()
        keyframeView.dirty = true
        i++
      }
      while (i < this.canvasKeyframes.length) {
        const keyframeView = this.canvasKeyframes[i]
        if (!keyframeView.visible) { break }
        keyframeView.visible = false
        keyframeView.setCoords()
        i++
      }

      this.canvas.requestRenderAll()
    },

    createSelectionRectangle () {
      const selectionView = new fabric.Rect({
        width: 10,
        height: 10,
        fill: '#fff17688',
        stroke: '#fdd835',
        strokeDashArray: [4, 4],
        selectable: false,
        evented: false,
        visible: false
      })

      this.addToCanvas([selectionView])

      this.selectionView = selectionView
    },

    updateSelectionView (corners) {
      let isVisible = false

      const view = this.selectionView
      if (corners) {
        const { corner1, corner2 } = corners

        if (corner1 && corner2) {
          const xMin = Math.min(corner1.x, corner2.x)
          const xMax = Math.max(corner1.x, corner2.x)
          const yMin = Math.min(corner1.y, corner2.y)
          const yMax = Math.max(corner1.y, corner2.y)

          isVisible = xMin !== xMax || yMin !== yMax

          if (isVisible) {
            view.left = xMin - this.canvas.viewportTransform[4]
            view.top = yMin
            view.width = xMax - xMin
            view.height = yMax - yMin
            view.setCoords()
          }
        }
      }

      view.visible = isVisible
      view.dirty = true
      this.canvas.requestRenderAll()
    },

    sendOperation (eventName, data) {
      const id = this.operationCounter++
      this.operationId = id
      this._operatingCache = { id, eventName, data }
      this.$emit(eventName, Object.assign({ id }, data))

      this.updateOperatingView()
    }
  }
}
</script>

<style lang="scss" scoped>
.component-container:focus {
  box-shadow: 0 0 4px var(--v-primary-base);
}

.timeline-canvas {
  width: 100%;
  height: 100%;
}
</style>
